/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Avatar,
  Button,
  Dropdown,
  Image,
  TableColumnsType,
  Tag,
  Tooltip,
} from "antd";

import { useEffect, useRef, useState } from "react";
import { PopUp } from "../../../atoms/PopUp";
import EmptyCard from "../../../atoms/EmptyCard";
import { CrossIcon, DeleteIcon, NeedHelp } from "../../../atoms/CustomIcons";
import ConfirmDelete from "../../../atoms/ConfirmDelete";
import { MoreOutlined } from "@ant-design/icons";
import DashboardTemplate from "../../../templates/Dashboard";
import Header from "../../../atoms/Header";
import {
  getCompanyInitials,
  getValueBeforeUnderscore,
  PAGE_LIMIT,
} from "../../../config/utils";
import Filters from "./Components/Filters";
import TaskModal from "./Components/TaskModal";
import { useAppDispatch, useAppSelector } from "../../../config/hooks";
import { getTaskListing, removeTaskListData } from "../ducks/actions";
import { deleteTask } from "../ducks/services";
import { AxiosError } from "axios";
import { ErrorMessage } from "../../../atoms/Toasts/ErrorMessage";
import { SuccessMessage } from "../../../atoms/Toasts/SuccessMessage";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import TableUi from "../../../atoms/Table";

export default function Tasks() {
  const dispatch = useAppDispatch();
  const location = window.location.pathname;
  const navigate = useNavigate();
  const pathSegments = location.split("/");
  const companyId = pathSegments[pathSegments.length - 1];
  const [pagination, setPagination] = useState(1);
  const [limit, setLimit] = useState(PAGE_LIMIT);
  const [tableRecord, setTableRecord] = useState<any | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [archivePopUp, setArchivePopUp] = useState(false);
  const dropdownRef = useRef<HTMLButtonElement>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<
    React.Key[] | string[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [archived, isArchived] = useState(false);
  const taskListData = useAppSelector(
    (state) => state?.workOrders?.taskListData
  );

  const [filters, setFilters] = useState<{
    type: undefined | string;
    status: undefined | string;
    state: undefined;
    city: undefined;
    last_user_activity: string[] | undefined;
    onboarding_complete_date: string[] | undefined;
    search_text: undefined | string;
  }>({
    type: undefined,
    status: undefined,
    state: undefined,
    city: undefined,
    last_user_activity: undefined,
    onboarding_complete_date: undefined,
    search_text: undefined,
  });

  const items = [
    {
      key: "1",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/edit-task/${tableRecord?.id}/${companyId}`);
          }}
          icon={<Image src="/images/table/edit-icon.svg" preview={false} />}
        >
          Edit details
        </Button>
      ),
    },
    {
      key: "2",
      label: (
        <Button
          type="text"
          onClick={(e) => {
            e.stopPropagation();
            setIsDeleteModalOpen(true);
            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
          }}
          className="delete-hover"
          icon={<DeleteIcon />}
        >
          Delete
        </Button>
      ),
    },
  ];

  const statusClassMap: Record<string, string> = {
    "in progress": "approval_pending_tag",
    open: "grey_tag",
    completed: "accepted_tag",
    scheduled: "sent_tag",
    closed: "cancelled_tag",
  };

  // const serviceLineClassMap: Record<string, string> = {
  //   "landscape maintenance": "green_tag",
  //   "lawn care": "orange_tag",
  //   irrigation: "violet_tag",
  //   construction: "yellow_tag",
  //   "Holiday lighting": "blue_tag",
  //   "Snow services": "red_tag",
  // };

  const columns: TableColumnsType = [
    {
      title: "Tasks",
      dataIndex: "name",
      width: 320,
      render: (text) => (
        <Tooltip title={text ? text : "-"}>
          <span className="table-col-ellipsis" style={{ width: "310px" }}>
            {text ? text : "-"}
          </span>
        </Tooltip>
      ),
      fixed: "left",
      sorter: (a, b) =>
        a.name?.localeCompare(b.name) || a.name?.length - b.name?.length,
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 147,
      sorter: (a, b) =>
        a.status?.localeCompare(b.status) ||
        a.status?.length - b.status?.length,
      render: (text) => {
        const cls = statusClassMap[text?.toLowerCase()] || "default_tag";
        return (
          <Tag
            className={cls}
            style={{ textTransform: "capitalize", border: 0 }}
          >
            {text}
          </Tag>
        );
      },
    },
    {
      title: "Priority",
      dataIndex: "priority",
      width: 140,
      render: (text) =>
        text ? (
          <span style={{ textTransform: "capitalize" }}> {text} </span>
        ) : (
          "-"
        ),
      sorter: (a, b) =>
        a.priority?.localeCompare(b.priority) ||
        a.priority?.length - b.priority?.length,
    },
    {
      title: "Due date",
      dataIndex: "due_date",
      width: 170,
      render: (text) => (text ? dayjs(text).format("ll") : "-"),
      sorter: (a, b) =>
        a.due_date?.localeCompare(b.due_date) ||
        a.due_date?.length - b.due_date?.length,
    },
    {
      title: "Occurrence",
      dataIndex: "occurrence",
      width: 140,
      // render: (text) => (
      //   <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
      //     <img src="/images/work-orders/reverse_horizontal.svg" alt="icon" />
      //     {text ? text : "-"}
      //   </span>
      // ),
      render: (text) => {
        if (!text) return "-";
        return (
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <img src="/images/work-orders/reverse_horizontal.svg" alt="icon" />
            {text}
          </span>
        );
      },
      sorter: (a, b) =>
        a.occurrence?.localeCompare(b.occurrence) ||
        a.occurrence?.length - b.occurrence?.length,
    },
    {
      title: "Scheduled date",
      dataIndex: "scheduled_date",
      width: 195,
      render: (text) => (text ? dayjs(text).format("ll") : "-"),
      sorter: (a, b) =>
        a.scheduled_date?.localeCompare(b.scheduled_date) ||
        a.scheduled_date?.length - b.scheduled_date?.length,
    },
    {
      title: "Property",
      dataIndex: "property_name",
      width: 270,
      render: (text) => (
        <Tooltip title={text ? text : "-"}>
          <span className="table-col-ellipsis" style={{ width: "260px" }}>
            {text ? text : "-"}
          </span>
        </Tooltip>
      ),
      sorter: (a, b) =>
        a.property_name?.localeCompare(b.property_name) ||
        a.property_name?.length - b.property_name?.length,
    },
    {
      title: "Contract",
      dataIndex: "contract_name",
      width: 270,
      render: (text) => (
        <Tooltip title={text ? text : "-"}>
          <span className="table-col-ellipsis" style={{ width: "260px" }}>
            {text ? text : "-"}
          </span>
        </Tooltip>
      ),
      sorter: (a, b) =>
        a.contract_name?.localeCompare(b.contract_name) ||
        a.contract_name?.length - b.contract_name?.length,
    },
    {
      title: "Service line",
      dataIndex: "service_line_name",
      width: 270,
      sorter: (a, b) =>
        a.service_line_name?.localeCompare(b.service_line_name) ||
        a.service_line_name?.length - b.service_line_name?.length,
      render: (text, record) => {
        if (text) {
          return (
            <Tag
              key={text.id}
              className={`defaultTag ${
                record?.service_line_color
                  ? getValueBeforeUnderscore(record?.service_line_color)
                  : "default_tag"
              }`}
              style={{ textTransform: "capitalize" }}
            >
              {text}
            </Tag>
          );
        }
        return "-";
      },
    },
    {
      title: "Service",
      dataIndex: "work_order_name",
      width: 200,
      render: (text) => (text ? text : "-"),
      sorter: (a, b) =>
        a.work_order_name?.localeCompare(b.work_order_name) ||
        a.work_order_name?.length - b.work_order_name?.length,
    },
    // {
    //   title: "Crew",
    //   dataIndex: "crew",
    //   width: 180,
    //   render: (text) =>
    //     text ? <Tag className="default_tag">{text}</Tag> : "-",
    //   sorter: (a, b) =>
    //     a.crew?.localeCompare(b.crew) || a.crew?.length - b.crew?.length,
    // },
    {
      title: "Production manager",
      dataIndex: "production_manager_name",
      width: 180,
      sorter: (a, b) =>
        a.production_manager_name?.name?.localeCompare(
          b.production_manager_name?.name
        ) ||
        a.production_manager_name?.name?.length -
          b.production_manager_name?.name?.length,
      render: (text, record) =>
        text ? (
          <div className="text_with_avatar">
            {record?.production_manager_image_url ? (
              <div className="participant_avatar">
                <Avatar src={record?.production_manager_image_url} />
              </div>
            ) : (
              <div className="participant_avatar">
                <Avatar>{getCompanyInitials(text)}</Avatar>
              </div>
            )}
            <span style={{ textTransform: "capitalize" }}>{text}</span>
          </div>
        ) : (
          "-"
        ),
    },
    {
      title: <Image src="/images/table/menu-burger.svg" preview={false} />,
      dataIndex: "action",
      width: 52,
      fixed: "right",
      render: (_text, record) => (
        <Dropdown
          menu={{ items }}
          trigger={["click"]}
          placement="bottomRight"
          className="actionButton"
        >
          <Button
            onClick={(e) => {
              e.stopPropagation();
              setTableRecord(record);
            }}
            onKeyDown={(e) => e.stopPropagation()}
            style={{
              padding: "6px",
              height: "auto",
              background: "transparent",
            }}
            className="only_icon"
            icon={<MoreOutlined />}
            ref={dropdownRef}
          />
        </Dropdown>
      ),
    },
  ];

  const deleteFunction = () => {
    setLoading(true);
    deleteTask(
      selectedRowKeys?.length > 0 ? selectedRowKeys : [tableRecord?.id || ""]
    )
      .then(() => {
        setLoading(false);
        setIsDeleteModalOpen(false);
        setSelectedRowKeys([]);
        const payload = {
          status: filters?.status ?? undefined,
          offset: pagination > 1 ? limit * (pagination - 1) : pagination - 1,
          limit: limit,
          company_id: companyId,
          // type: filters?.type ?? undefined,
          search_text: filters?.search_text ?? undefined,
        };
        dispatch(getTaskListing(payload));
        SuccessMessage("Task deleted");
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  const deleteClientPopUp = {
    visibility: isDeleteModalOpen,
    content: (
      <ConfirmDelete
        title=""
        onClose={() => setIsDeleteModalOpen(false)}
        heading="Confirm deletion"
        paragraph={
          selectedRowKeys?.length > 0 ? (
            <>
              Are you sure you want to delete selected task?
              <br /> <br />
              This action cannot be undone.
            </>
          ) : tableRecord?.do_every_visit ? (
            <>
              Are you sure you want to delete this task occurrence?
              <br />
              <br />
              Other occurrences will remain, and the total count will be reduced
              by one.
              <br />
              <br />
              This action cannot be undone.
            </>
          ) : (
            <>
              Are you sure you want to delete this task?
              <br />
              <br />
              This action cannot be undone.
            </>
          )
        }
        btnText={
          selectedRowKeys?.length > 0
            ? `Delete ${
                selectedRowKeys?.length === 1
                  ? selectedRowKeys?.length + " task"
                  : selectedRowKeys?.length + " tasks"
              }`
            : "Delete task"
        }
        btnApi={deleteFunction}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setIsDeleteModalOpen(false),
  };

  const taskDetailModal = {
    visibility: isTaskModalOpen,
    class: "Task_Modal",
    content: (
      <TaskModal
        onClose={() => setIsTaskModalOpen(false)}
        tableRecord={tableRecord}
        loading={loading}
        setLoading={setLoading}
        companyId={companyId}
      />
    ),
    width: 1200,
    onCancel: () => setIsTaskModalOpen(false),
  };

  const archiveFunction = () => {
    setLoading(true);
  };

  const confirmArchivedPopUp = {
    visibility: archivePopUp,
    content: (
      <ConfirmDelete
        title=""
        isArchivePopup={true}
        onClose={() => setArchivePopUp(false)}
        heading='{tableRecord?.status === "active" ? "Archive" : "Activate"}'
        paragraph={
          <>
            Are you sure you want to{" "}
            {/* {tableRecord?.status === "active" ? "archive" : "activate"}{" "}
            <b>{tableRecord?.name}</b>? <br /> <br />
            {tableRecord?.status === "active" && (
              <>This task won’t be available for use.</>
            )} */}
          </>
        }
        btnText={`${
          "Activate"
          // tableRecord?.status === "active" ? "Archive" : "Activate"
        } task`}
        btnApi={archiveFunction}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setArchivePopUp(false),
  };

  const onClickRow = (record: any) => {
    return {
      onClick: () => {
        setTableRecord(record);
        setIsTaskModalOpen(true);
      },
    };
  };

  useEffect(() => {
    dispatch(removeTaskListData());
  }, [dispatch]);

  useEffect(() => {
    if (companyId) {
      const payload = {
        status: filters?.status ?? undefined,
        offset: pagination > 1 ? limit * (pagination - 1) : pagination - 1,
        limit: limit,
        company_id: companyId,
        // type: filters?.type ?? undefined,
        search_text: filters?.search_text ?? undefined,
      };
      dispatch(getTaskListing(payload));
    }
  }, [dispatch, filters, pagination, limit, companyId]);

  return (
    <DashboardTemplate loading={loading}>
      <Header
        title="Work orders & tasks"
        // description="View and manage your tasks and subcontractors."
        btnText1="Need help?"
        btnIcon1={<NeedHelp />}
      />
      <TableUi
        columns={columns}
        data={{
          count: taskListData?.count || 0,
          data: taskListData?.data || [],
        }}
        filters={
          <Filters
            setFilters={setFilters}
            filters={filters}
            archived={archived}
            isArchived={isArchived}
            companyId={companyId}
          />
        }
        pageSize={limit}
        setPageSize={setLimit}
        setCurrentPage={setPagination}
        currentPage={pagination}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
        createButtonText="New task"
        createButtonLink={`/new-task/${companyId}`}
        onClickRow={onClickRow}
      />
      {taskListData?.count === 0 && (
        <div className="empty_wrap">
          <EmptyCard
            imageHeight={220}
            title={
              archived
                ? "There are no completed tasks"
                : filters?.search_text
                ? "No results match the filter or search criteria"
                : `There are no ${filters?.status ?? "active"} tasks`
            }
            description={
              archived
                ? "We believe in our absolute success and hope to never have an completed tasks here."
                : filters?.search_text
                ? "Try adjusting the filters and search."
                : ""
            }
            image={
              archived
                ? "/images/empty-icons/no-archived.svg"
                : "/images/empty-icons/no-active.svg"
            }
          />
        </div>
      )}

      {selectedRowKeys?.length !== 0 ? (
        <div className="selection_bar">
          <div className="selected_count">
            <Button
              icon={<CrossIcon style={{ color: "white" }} />}
              className="only_icon"
              type="text"
              onClick={() => setSelectedRowKeys([])}
            />
            {selectedRowKeys.length}{" "}
            {`${selectedRowKeys.length === 1 ? "task" : "tasks"}`} selected
          </div>
          <div className="selection_buttons">
            <Button
              type="text"
              icon={<DeleteIcon />}
              onClick={() => {
                setIsDeleteModalOpen(true);
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      ) : null}

      <PopUp {...deleteClientPopUp} />
      <PopUp {...confirmArchivedPopUp} />
      <PopUp {...taskDetailModal} />
    </DashboardTemplate>
  );
}
